import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { LayoutTemplate, Settings, Trash2, Image as ImageIcon, Link as LinkIcon , Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FlowNodeProps, ContentBlock } from '@/types/flow';
import BlocksPreview from './BlocksPreview';
import { ButtonStat } from '../../stats/FunnelStatsContext';
import { versaoLeve, aoFalharVersaoLeve } from '@/lib/mediaLeve';

const PhotoTemplateNode = ({ data, selected }: FlowNodeProps) => {
    const config = data.config || {};
    const title = config.title || '';
    const subtitle = config.subtitle || '';
    const buttons = data.buttons || [];
    const hasImage = !!config.media_url;

    const allButtonsAreUrl = buttons.length > 0 && buttons.every((btn) => btn.button_type === 'url');

    return (
        <div
            className={`
                bg-white rounded-lg border-l-[3px] border-l-cyan-500 shadow-sm min-w-[260px] max-w-[300px]
                transition-all duration-150
                ${selected ? 'shadow-md ring-2 ring-blue-400/40' : 'border-t border-r border-b border-gray-200 hover:shadow-md'}
            `}
            style={{ borderTop: selected ? undefined : '1px solid #e5e7eb', borderRight: selected ? undefined : '1px solid #e5e7eb', borderBottom: selected ? undefined : '1px solid #e5e7eb' }}
        >
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-cyan-50 rounded-lg flex items-center justify-center">
                        <LayoutTemplate className="w-3.5 h-3.5 text-cyan-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[13px] text-gray-800 block truncate">{data.node_name || 'Template de Foto'}</span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Foto + Botões</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-3 py-2.5 space-y-2">
                {hasImage ? (
                    <div className="relative aspect-video bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                        <img
                            src={versaoLeve(config.media_url)}
                            onError={(e) => aoFalharVersaoLeve(e, config.media_url)}
                            alt="Preview"
                            loading="lazy"
                            className="w-full h-full object-cover"
                        />
                    </div>
                ) : (
                    <div className="aspect-video bg-gray-50 rounded-lg flex items-center justify-center border border-dashed border-gray-200">
                        <ImageIcon className="w-6 h-6 text-gray-300" />
                    </div>
                )}

                {(title || subtitle) && (
                    <div>
                        {title && <p className="text-[12px] font-semibold text-gray-800 line-clamp-2">{title}</p>}
                        {subtitle && <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">{subtitle}</p>}
                    </div>
                )}

                {buttons.length > 0 && (
                    <div className="space-y-1.5 pt-1.5 border-t border-gray-100">
                        {buttons.slice(0, 3).map((button, idx) => (
                            <div
                                key={button.id || idx}
                                className="relative text-[11px] bg-cyan-50 border border-cyan-100 rounded-md px-3 py-2 text-cyan-700 text-center font-medium flex items-center justify-center gap-1.5 min-h-[34px] overflow-visible"
                            >
                                {button.button_type === 'url' && <LinkIcon className="w-3 h-3 flex-shrink-0" />}
                                <span className="truncate flex-1">{button.button_text || `Botão ${idx + 1}`}</span>
                                <ButtonStat nodeId={data.id} buttonId={button.id} />

                                {button.button_type === 'postback' && (
                                    <Handle
                                        type="source"
                                        position={Position.Right}
                                        id={`button-${button.id}`}
                                        style={{
                                            position: 'absolute',
                                            right: '-10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: '14px',
                                            height: '14px',
                                            background: '#2196F3',
                                            border: '2px solid white',
                                            borderRadius: '50%',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            cursor: 'crosshair',
                                            zIndex: 10,
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                        {buttons.length > 3 && (
                            <span className="text-[10px] text-gray-400 block text-center">+{buttons.length - 3} botões</span>
                        )}
                    </div>
                )}
            </div>

            <BlocksPreview blocks={(config.blocks || []) as ContentBlock[]} />

            <div className="border-t border-gray-100 px-3 py-1.5 flex gap-1">
                <Button size="sm" variant="ghost" className="flex-1 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50/50 h-7 text-[11px]" onClick={() => data.onEdit?.(data.id)}>
                    <Settings className="w-3 h-3 mr-1" />
                    Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 h-7 px-2" title="Duplicar" onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}>
                    <Copy className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 hover:bg-red-50/50 h-7 px-2" onClick={() => data.onDelete?.(data.id)}>
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>

            <Handle type="target" position={Position.Left} id="target" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />

            {(allButtonsAreUrl || buttons.length === 0) && (
                <Handle type="source" position={Position.Right} id="source" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            )}
        </div>
    );
};

export default memo(PhotoTemplateNode);
